import {
  TextField,
  Switch,
  Select,
  MenuItem,
  Slider,
  FormControl,
  InputLabel,
  FormHelperText,
  FormControlLabel,
} from '@mui/material';
import { DataType, type MessageFieldDefinition } from '@k-ucap/shared';
import { OperatingMode, OperatingState, AlertLevel } from '@k-ucap/message-dictionary';
import type { FieldValidationResult } from '@/domain/message/MessageValidationService.js';

const enumOptions: Record<string, string[]> = {
  OperatingMode: Object.values(OperatingMode),
  OperatingState: Object.values(OperatingState),
  AlertLevel: Object.values(AlertLevel),
};

const numericDataTypes: string[] = [
  DataType.UINT8,
  DataType.INT8,
  DataType.UINT16,
  DataType.INT16,
  DataType.UINT32,
  DataType.INT32,
  DataType.FLOAT32,
  DataType.FLOAT64,
];

const autoControl = (dataType: string): string => {
  if (dataType === DataType.BOOLEAN) return 'switch';
  if (dataType === DataType.ENUM) return 'select';
  if (dataType === DataType.BYTES) return 'hex';
  if (numericDataTypes.includes(dataType)) return 'number';
  return 'text';
};

const parseHex = (text: string): number[] => {
  if (!text.trim()) return [];
  return text
    .split(/[\s,]+/)
    .map((t) => t.trim())
    .filter(Boolean)
    .map((t) => {
      const value = t.startsWith('0x') ? parseInt(t.slice(2), 16) : parseInt(t, 16);
      return Number.isNaN(value) ? 0 : value;
    });
};

interface DynamicFieldProps {
  field: MessageFieldDefinition;
  value: unknown;
  onChange: (key: string, value: unknown) => void;
  errors?: FieldValidationResult[];
  disabled?: boolean;
}

export const DynamicField = ({ field, value, onChange, errors, disabled }: DynamicFieldProps) => {
  const control = field.uiControl ?? autoControl(field.dataType);
  const hasFail = errors?.some((e) => e.status === 'FAIL');
  const helperText = [
    errors?.map((e) => e.message).join(' / '),
    field.description,
    field.unit ? `단위: ${field.unit}` : undefined,
  ]
    .filter(Boolean)
    .join(' | ');

  const handleNumberChange = (text: string) => {
    if (text === '') {
      onChange(field.key, undefined);
      return;
    }
    const num = Number(text);
    onChange(field.key, Number.isNaN(num) ? undefined : num);
  };

  const label = `${field.displayName}${field.required ? ' *' : ''}`;

  if (control === 'switch' || control === 'checkbox') {
    return (
      <FormControl fullWidth error={hasFail}>
        <FormControlLabel
          control={
            <Switch
              checked={Boolean(value)}
              onChange={(e) => onChange(field.key, e.target.checked)}
              disabled={disabled}
            />
          }
          label={label}
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (control === 'select' || field.dataType === DataType.ENUM) {
    const options = enumOptions[field.enumRef ?? ''] ?? [];
    return (
      <FormControl fullWidth error={hasFail}>
        <InputLabel>{label}</InputLabel>
        <Select
          value={typeof value === 'string' ? value : ''}
          label={label}
          onChange={(e) => onChange(field.key, e.target.value)}
          disabled={disabled}
        >
          {options.map((opt) => (
            <MenuItem key={opt} value={opt}>
              {opt}
            </MenuItem>
          ))}
        </Select>
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (control === 'slider') {
    return (
      <FormControl fullWidth>
        <FormHelperText>{label}</FormHelperText>
        <Slider
          value={typeof value === 'number' ? value : 0}
          min={field.minimum ?? 0}
          max={field.maximum ?? 100}
          step={field.precision ?? 0.1}
          onChange={(_e, v) => onChange(field.key, v)}
          disabled={disabled}
          valueLabelDisplay="auto"
        />
        {helperText && <FormHelperText>{helperText}</FormHelperText>}
      </FormControl>
    );
  }

  if (control === 'textarea') {
    return (
      <TextField
        fullWidth
        multiline
        rows={3}
        label={label}
        value={typeof value === 'string' ? value : ''}
        onChange={(e) => onChange(field.key, e.target.value)}
        error={hasFail}
        helperText={helperText}
        disabled={disabled}
      />
    );
  }

  if (control === 'hex' || field.dataType === DataType.BYTES) {
    const displayValue = Array.isArray(value) ? value.map((v) => v.toString(16).padStart(2, '0')).join(' ') : '';
    return (
      <TextField
        fullWidth
        label={label}
        value={displayValue}
        onChange={(e) => onChange(field.key, parseHex(e.target.value))}
        error={hasFail}
        helperText={helperText || 'HEX 값을 공백/쉼표로 구분하여 입력 (예: AA BB CC)'}
        disabled={disabled}
        placeholder="AA BB CC"
      />
    );
  }

  if (
    ['number', 'latitude', 'longitude', 'angle', 'datetime'].includes(control) ||
    numericDataTypes.includes(field.dataType)
  ) {
    const step = control === 'latitude' || control === 'longitude' ? 0.000001 : (field.precision ?? 0.1);
    return (
      <TextField
        fullWidth
        type="number"
        label={label}
        value={typeof value === 'number' ? value : ''}
        onChange={(e) => handleNumberChange(e.target.value)}
        slotProps={{
          htmlInput: {
            min: field.minimum,
            max: field.maximum,
            step,
          },
        }}
        error={hasFail}
        helperText={helperText}
        disabled={disabled}
      />
    );
  }

  return (
    <TextField
      fullWidth
      label={label}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange(field.key, e.target.value)}
      error={hasFail}
      helperText={helperText}
      disabled={disabled}
    />
  );
};
